import { ForgotPasswordPage } from '@components/Login/ForgotPassword';
import { InvitationPage } from '@components/Login/Invitation';
import AlertDetail from '@pages/AlertDetail';
import { Alerts, AlertWorkbook } from '@pages/Alerts';
import Forecasts from '@pages/Forecasts';
import Home from '@pages/Home';
import Inventory from '@pages/Inventory';
import Replenishment from '@pages/Replenishment';
import Reports from '@pages/Reports';
import SavedReportsLibrary from '@pages/SavedReportsLibrary';
import Distribution from '@pages/Distribution';
import Rebalance from '@pages/Rebalance';
import Login from '@pages/Login';
import PurchaseOrders from '@pages/PurchaseOrders';
import ProcurementOrders from '@pages/ProcurementOrders';
import Orders from '@pages/Orders';
import StockTransfers from '@pages/StockTransfers';
import StockTransferDetail from '@pages/StockTransferDetail';
import Payments from '@pages/Payments';
import Shipments from '@pages/Shipments';
import Settings from '@pages/Settings';
import ScheduledEmails from '@pages/ScheduledEmails';
import Sales from '@pages/Sales';
import SalesOrders from '@pages/SalesOrders';
import B2bCustomerAccount from '@pages/B2bCustomerAccount';
import B2bCustomers from '@pages/B2bCustomers';
import B2bPricing from '@pages/B2bPricing';
import NewB2bOrder from '@pages/NewB2bOrder';
import PortalHome from '@pages/PortalHome';
import PortalNewOrder from '@pages/PortalNewOrder';
import PortalOrderDetail from '@pages/PortalOrderDetail';
import PortalOrders from '@pages/PortalOrders';
import SalesOrderDetail from '@pages/SalesOrderDetail';
import Suppliers from '@pages/Suppliers';
import CmSiteDetail from '@pages/CmSiteDetail';
import Trace from '@pages/Trace';
import VendorTrace from '@pages/VendorTrace';
import CmSites from '@pages/CmSites';
import ProductionMonitor from '@pages/ProductionMonitor';
import VendorHistory from '@pages/VendorHistory';
import VendorHome from '@pages/VendorHome';
import VendorReceive from '@pages/VendorReceive';
import VendorStock from '@pages/VendorStock';
import VendorBom from '@pages/VendorBom';
import VendorRmPm from '@pages/VendorRmPm';
import VendorProductionOrders from '@pages/VendorProductionOrders';
import VendorDispatch from '@pages/VendorDispatch';
import VendorUpload from '@pages/VendorUpload';
import ProductionPlanning from '@pages/ProductionPlanning';
import WorkOrders from '@pages/WorkOrders';
import WorkOrderDetail from '@pages/WorkOrderDetail';
import WarehouseInbound from '@pages/WarehouseInbound';
import WarehouseOutbound from '@pages/WarehouseOutbound';
import AcceptConsultantInvite from '@pages/AcceptConsultantInvite';
import AcceptVendorInvite from '@pages/AcceptVendorInvite';
import AcceptCustomerInvite from '@pages/AcceptCustomerInvite';
import AcceptTeamInvite from '@pages/AcceptTeamInvite';
import AmazonMarketplaceSelect from '@pages/AmazonMarketplaceSelect';
import PrivateRoute from '@routes/heimdall/PrivateRoute';
import { RoleAwareLayout } from '@routes/heimdall/RoleAwareLayout';
import { Navigate, Outlet, type RouteObject } from 'react-router';

import EmailRenderTable from '@pages/EmailRenderTable';
import Signup from '@pages/Signup';

const FALLBACK_ROUTE: RouteObject = {
    path: '*',
    element: <Navigate to="/" />,
};

import PublicOnlyRoute from '@routes/heimdall/PublicOnlyRoute';

const PUBLIC_ROUTES: RouteObject[] = [
    {
        // Headless email-render surface: opened by the report worker's puppeteer with a
        // short-lived token in the payload — deliberately NOT wrapped in PublicOnlyRoute
        // (that would bounce an "authenticated" headless session to the app).
        path: '/email-render/table',
        element: <EmailRenderTable />,
    },
    {
        path: '/signup',
        element: (
            <PublicOnlyRoute>
                <Signup />
            </PublicOnlyRoute>
        ),
    },
    {
        path: '/login',
        element: (
            <PublicOnlyRoute>
                <Login />
            </PublicOnlyRoute>
        ),
    },
    {
        path: '/forgot-password',
        element: (
            <PublicOnlyRoute>
                <ForgotPasswordPage />
            </PublicOnlyRoute>
        ),
    },
    {
        path: '/invite',
        element: <InvitationPage />,
    },
    {
        path: '/invite/consultant/:token',
        element: <AcceptConsultantInvite />,
    },
    {
        // Public like its consultant twin - the holder has no account yet; the link is the way in.
        path: '/invite/vendor/:token',
        element: <AcceptVendorInvite />,
    },
    {
        // A B2B customer's portal invitation - same shape, lands in the customer portal.
        path: '/invite/customer/:token',
        element: <AcceptCustomerInvite />,
    },
    {
        // A colleague's invitation to the brand itself - lands in the main app as an admin.
        path: '/invite/team/:token',
        element: <AcceptTeamInvite />,
    },
    {
        path: '/amazon/connect',
        element: <AmazonMarketplaceSelect />,
    },
    {
        path: '/verify-email',
        element: <></>,
    },
    {
        path: '/join-organization',
        element: <></>,
    },
];

const PRIVATE_ROUTES: RouteObject[] = [
    {
        path: '/',
        element: (
            <PrivateRoute>
                <RoleAwareLayout />
            </PrivateRoute>
        ),
        children: [
            {
                index: true,
                element: <Navigate to="/home" replace />,
            },
            {
                path: '/home',
                element: <Home />,
            },
            {
                path: '/alerts',
                element: <Alerts />,
            },
            {
                path: '/alerts/new',
                element: <AlertWorkbook />,
            },
            {
                path: '/alert/:alertId',
                element: <AlertDetail />,
            },
            {
                path: '/forecasts',
                element: <Forecasts />,
            },
            {
                path: '/inventory',
                element: <Inventory />,
            },
            {
                path: '/replenishment',
                element: <Replenishment />,
            },
            {
                path: '/reports',
                element: <Reports />,
            },
            {
                path: '/reports/library',
                element: <SavedReportsLibrary />,
            },
            {
                path: '/rebalance',
                element: <Rebalance />,
            },
            {
                path: '/distribution',
                element: <Distribution />,
            },
            {
                path: '/risks',
                element: <Home />,
            },
            {
                path: '/sales',
                element: <Sales />,
            },
            {
                path: '/sales-orders',
                element: <SalesOrders />,
            },
            {
                path: '/sales-orders/:id',
                element: <SalesOrderDetail />,
            },
            // Selling to B2B customers (brand side); orders themselves live on Sales Orders.
            { path: '/b2b/customers', element: <B2bCustomers /> },
            { path: '/b2b/customers/:customerId', element: <B2bCustomerAccount /> },
            { path: '/b2b/pricing', element: <B2bPricing /> },
            { path: '/b2b/orders/new', element: <NewB2bOrder /> },
            // The customer portal (B2B customer logins only; see RoleAwareLayout).
            { path: '/portal/home', element: <PortalHome /> },
            { path: '/portal/orders', element: <PortalOrders /> },
            { path: '/portal/orders/new', element: <PortalNewOrder /> },
            { path: '/portal/orders/:id', element: <PortalOrderDetail /> },
            {
                path: '/suppliers',
                element: <Suppliers />,
            },
            {
                path: '/purchase-orders',
                element: <PurchaseOrders />,
            },
            {
                path: '/procurement-orders',
                element: <ProcurementOrders />,
            },
            {
                path: '/orders',
                element: <Orders />,
            },
            {
                path: '/shipments',
                element: <Shipments />,
            },
            {
                path: '/stock-transfers',
                element: <StockTransfers />,
            },
            {
                path: '/stock-transfers/:id',
                element: <StockTransferDetail />,
            },
            {
                path: '/payments',
                element: <Payments />,
            },
            {
                path: '/settings',
                element: <Settings />,
            },
            {
                path: '/scheduled-emails',
                element: <ScheduledEmails />,
            },
            {
                path: '/trace',
                element: <Trace />,
            },
            {
                path: '/vendor/trace',
                element: <VendorTrace />,
            },
            {
                path: '/cm-sites',
                element: <CmSites />,
            },
            {
                path: '/cm-sites/:warehouseId',
                element: <CmSiteDetail />,
            },
            {
                path: '/production-monitor',
                element: <ProductionMonitor />,
            },
            {
                path: '/vendor/home',
                element: <VendorHome />,
            },
            {
                path: '/vendor/history',
                element: <VendorHistory />,
            },
            {
                path: '/vendor/receive',
                element: <VendorReceive />,
            },
            {
                path: '/vendor/stock',
                element: <VendorStock />,
            },
            {
                path: '/vendor/rm-pm',
                element: <VendorRmPm />,
            },
            {
                path: '/vendor/bom',
                element: <VendorBom />,
            },
            {
                path: '/vendor/production-orders',
                element: <VendorProductionOrders />,
            },
            {
                path: '/vendor/dispatch',
                element: <VendorDispatch />,
            },
            {
                path: '/vendor/upload',
                element: <VendorUpload />,
            },
            {
                path: '/production-planning',
                element: <ProductionPlanning />,
            },
            {
                path: '/work-orders',
                element: <WorkOrders />,
            },
            {
                path: '/work-orders/:id',
                element: <WorkOrderDetail />,
            },
            {
                path: '/warehouse-ops/inbound',
                element: <WarehouseInbound />,
            },
            {
                path: '/warehouse-ops/outbound',
                element: <WarehouseOutbound />,
            },
        ],
    },
];

const WebAppRoutes = [...PUBLIC_ROUTES, ...PRIVATE_ROUTES, FALLBACK_ROUTE];
export default WebAppRoutes;
